import React, { useEffect, useState } from "react";
import "./App.scss";

import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import Navbar from "./components/Navbar/Navbar";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom";
import AddProduct from "./pages/Product/AddProduct/AddProduct";
import ManageProduct from "./pages/Product/ManageProduct/ManageProduct";
import ProtectedRoutes from "./helpers/ProtectedRoutes";
import { getUser } from "./api/auth";
import { useDispatch, useSelector } from "react-redux";
import {
  clearUser,
  updateUser,
  updateUserToken,
} from "./redux/features/user-slice";
import { fetchOrganizations } from "./api/organization";
import {
  clearOrganization,
  updateCategories,
  updateDeliveries,
  updateLoadingOrg,
  updateOrganization,
  updateOrganizationSubscription,
  updateTaxRate,
} from "./redux/features/organization-slice";
import {
  clearBranches,
  updateActiveBranch,
  updateBranches,
} from "./redux/features/branch-slice";
import Loader from "./components/Loader/Loader";
import AddOrganization from "./pages/Organization/AddOrganization/AddOrganization";
import MyOrganization from "./pages/Organization/MyOrganization/MyOrganization";
import { fetchCategoriesByOrganization } from "./api/categories";

import ROUTES from "./constants/routes";
import Checkout from "./pages/Checkout/Checkout";
import Cart from "./pages/Checkout/Cart/Cart";
import { fetchTaxRate } from "./api/settings";
import Payment from "./pages/Checkout/Payment/Payment";
import Sales from "./pages/Sales/Sales";
import Orders from "./pages/Orders/Orders";
import SalesDetails from "./pages/Sales/SalesDetails/SalesDetails";
import Customer from "./pages/Customer/Customer";
import AddCustomer from "./pages/Customer/AddCustomer/AddCustomer";
import CustomerDetails from "./pages/Customer/CustomerDetails/CustomerDetails";
import StoreCredit from "./pages/Customer/StoreCredit/StoreCredit";
import ManageBranch from "./pages/Organization/ManageBranch/ManageBranch";
import Categories from "./pages/Categories/Categories";
import AddCategory from "./pages/Categories/AddCategory/AddCategory";
import Setting from "./pages/Setting/Setting";
import Analytics from "./pages/Analytics/Analytics";

import MainAnalytics from "./pages/Analytics/Main/MainAnalytics";
import RevenueAnalytics from "./pages/Analytics/Revenue/RevenueAnalytics";
import SalesAnalytics from "./pages/Analytics/Sales/SalesAnalytics";
import SalesByStaffAnalytics from "./pages/Analytics/SalesByStaff/SalesByStaffAnalytics";
import SalesByCustomerAnalytics from "./pages/Analytics/SalesByCustomer/SalesByCustomerAnalytics";
import ProfitsAnalytics from "./pages/Analytics/Profits/ProfitsAnalytics";
import PaymentAnalytics from "./pages/Analytics/Payment/PaymentAnalytics";
import ProductAnalytics from "./pages/Analytics/Products/ProductAnalytics";
import BranchAnalysts from "./pages/Analytics/Branch/BranchAnalysts";
import PDF from "./pages/PDF/PDF";
import Login from "./pages/Login/Login";
import Register from "./pages/Register/Register";
import ForgotPassword from "./pages/Forgot/ForgotPassword/ForgotPassword";
import ResetPassword from "./pages/Forgot/ResetPassword/ResetPassword";
import VerifyOTP from "./pages/Forgot/VerifyOTP/VerifyOTP";
import Home from "./pages/Home/Home";
import GeneralSetting from "./pages/Setting/General/General";
import Currency from "./pages/Setting/General/Currency/Currency";
import DeliveryRates from "./pages/Setting/Delivery/DeliveryRates";
import AddDelivery from "./pages/Setting/Delivery/AddDelivery/AddDelivery";
import PaymentOption from "./pages/Setting/Payment/PaymentOption";
import Activities from "./pages/Setting/Activities/Activities";
import Upgrade from "./pages/Upgrade/Upgrade";
import UpgradeAd from "./pages/Upgrade/UpgradeAd/UpgradeAd";
import LearnMore from "./pages/Upgrade/LearnMore/LearnMore";
import Users from "./pages/Users/Users";
import AddUser from "./pages/Users/AddUser/AddUser";
import CreatePassword from "./pages/Users/AddUser/CreatePassword/CreatePassword";
import UserBranches from "./pages/Users/UserBranches/UserBranches";
import UpgradePayment from "./pages/Upgrade/Payment/UpgradePayment";
import {
  clearSubscriptionPlans,
  updateFreeTrailStatus,
  updateSubscriptionFeatures,
  updateSubscriptionPlans,
} from "./redux/features/subscription-slice";
import {
  fetchSubscriptionFeatures,
  fetchSubscriptionPlans,
} from "./api/subscription";
import CategoryAnalytics from "./pages/Analytics/Categories/Categories";

import { GoogleOAuthProvider } from "@react-oauth/google";
import Onboard from "./pages/Onboard/Onboard";
import Tour from "./pages/Onboard/Tour";
import GoogleAnalytics from "./components/GoogleAnalytics/GoogleAnalytics";
import PageTracker from "./components/GoogleAnalytics/PageTracker";
import {
  clearHomeData,
  fetchCustomerData,
  fetchProductData,
  fetchSalesData,
  updateNetworkStatus,
} from "./redux/features/home-slice";
import Help from "./pages/Help/Help";
import Support from "./pages/Help/Support/Support";
import Contactus from "./pages/Help/Contactus/Contactus";
import SupportDetail from "./pages/Help/Support/SupportDetail/SupportDetail";
import PullToRefresh from "./components/PullToRefresh/PullToRefresh";
import Import from "./pages/Import/Import";
import Profile from "./pages/Profile/Profile";
import ContactSupportModel from "./pages/Help/ContactSupportModel";
import OrdersSales from "./pages/Setting/OrdersSales/OrdersSales";
import FreeTrail from "./pages/Upgrade/FreeTrail/FreeTrail";
import Cookies from "./components/Cookies/Cookies";
import { setApiToken } from "./api/api";
import { Capacitor } from "@capacitor/core";
import { clearAnalytics } from "./redux/features/analytics-slice";
import { clearCart } from "./redux/features/cart-slice";

// AI Chat Page Import
import AIChatPage from "./pages/AIChat/AIChatPage";
import { clearProducts } from "./redux/features/products-slice";
import { storage } from "./helpers/storage";
import { isActuallyOnline, transferQueuedData } from "./helpers/network";
import { clearSales } from "./redux/features/sales-slice";
import { clearOrders } from "./redux/features/orders-slice";
import { clearCustomers } from "./redux/features/customers-slice";
import { fetchDeliveries } from "./api/delivery";
import initViewportHeight from "./helpers/viewPortDetector";
import { MdOutlineWifi, MdOutlineWifiOff } from "react-icons/md";
import NetworkTab from "./components/NetworkTab/NetworkTab";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const NewApp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const organization = useSelector((state) => state.organizationReducer.value);
  const branch = useSelector((state) => state.branchReducer.value);
  const user = useSelector((state) => state.userReducer.value);
  const { networkStatus } = useSelector((state) => state.homeReducer.value);

  const [loadingUser, setLoadingUser] = useState(true);

  const getUserDetails = async () => {
    setLoadingUser(true);
    // Safely construct publicPaths
    const safeGetPath = (routeKey) =>
      ROUTES[routeKey] ? ROUTES[routeKey].path : undefined;

    const publicPaths = [
      safeGetPath("login"),
      safeGetPath("register"),
      safeGetPath("forgotPassword"),
      safeGetPath("resetPassword"),
      safeGetPath("verifyOTP"),
      safeGetPath("contactDUS"),
      safeGetPath("onboard"),
      safeGetPath("tour"),
      safeGetPath("aiChat"),
    ].filter((path) => path !== undefined); // Filter out any undefined paths

    const authToken = await storage.get("authToken");
    const expiresAt = await storage.get("expiresAt");
    const userStorageData = await storage.get("userData");

    let isExpired = false;    

    if(expiresAt){
      const expiryDate = new Date(expiresAt);
      const now = new Date();
      isExpired = now > expiryDate;      
    }

    if (authToken && !isExpired) {
      const user = JSON.parse(userStorageData);

      if (user?.id) {
        dispatch(updateUser(user));
        const tokenData = {
          token: authToken,
        };

        dispatch(updateUserToken(tokenData));
        setLoadingUser(false);
      }
      getUser(authToken)
        .then((res) => {
          if (res.data.user) {
            storage.set("userData", JSON.stringify(res.data.user));
            dispatch(updateUser(res.data.user));
          }
        })
        .catch((error) => {
          if (
            error.response &&
            error.response.data &&
            error.response.data.message === "Unauthenticated."
          ) {
            storage.removeAll();
          }
        })
        .finally(() => {
          setLoadingUser(false);
        });
    } else {
      if (!publicPaths.includes(location.pathname)) {
        navigate(ROUTES.login.path);
      }
      setLoadingUser(false);
      dispatch(clearUser());
      dispatch(clearProducts());
      dispatch(clearAnalytics());
      dispatch(clearBranches());
      dispatch(clearCart());
      dispatch(clearHomeData());
      dispatch(clearOrganization());
      dispatch(clearSubscriptionPlans());
      dispatch(clearSales());
      dispatch(clearOrders());
      dispatch(clearCustomers());
      storage.removeAll();
    }
  };

  const getOrganization = async () => {
    if (!loadingUser && user.token) {
      dispatch(updateLoadingOrg(true));

      const storageOrganization = await storage.get("organization");
      const storageOrganizationSubscription = await storage.get(
        "organizationSubscription"
      );
      const storageBranches = await storage.get("branches");
      const storageActiveBranch = await storage.get("activeBranch");
      if (
        storageOrganization &&
        storageOrganizationSubscription &&
        storageBranches &&
        storageActiveBranch
      ) {
        dispatch(updateOrganization(JSON.parse(storageOrganization)));
        dispatch(
          updateOrganizationSubscription({
            subscription: JSON.parse(storageOrganizationSubscription),
          })
        );
        dispatch(updateBranches(JSON.parse(storageBranches)));
        dispatch(updateActiveBranch(JSON.parse(storageActiveBranch)));
        dispatch(updateLoadingOrg(false));
      }

      fetchOrganizations()
        .then((res) => {
          if (res.data?.id) {
            const organization = {
              ...res.data,
              currency: res.data?.currency_symbol,
              currencyCode: res.data?.currency,
            };
            storage.set("organization", JSON.stringify(organization));
            dispatch(updateOrganization(organization));
            const organizationSubscription = organization?.subscription || {};
            storage.set(
              "organizationSubscription",
              JSON.stringify(organizationSubscription)
            );
            dispatch(
              updateOrganizationSubscription({
                subscription: organizationSubscription,
              })
            );

            const branches = organization?.branches || [];
            if (branches?.length > 0) {
              const previousActiveBranch = storageActiveBranch
                ? JSON.parse(storageActiveBranch)
                : {};
              const exists = previousActiveBranch
                ? branches?.find((el) => el?.id === previousActiveBranch?.id)
                : null;
              const selectedBranch = exists || branches[0];
              const branchesData = {
                branches: branches,
              };
              storage.set("branches", JSON.stringify(branchesData));
              storage.set("activeBranch", JSON.stringify(selectedBranch));
              dispatch(updateBranches(branchesData));
              dispatch(updateActiveBranch(selectedBranch));
            } else {
              navigate(ROUTES.myOrganization.path);
            }

            if (res.data?.is_free_trail) {
              dispatch(updateFreeTrailStatus({ free_trail: true }));
            }
          }
        })
        .catch((err) => {})
        .finally(() => {
          dispatch(updateLoadingOrg(false));
        });
    }
  };

  const getCategories = async () => {
    const storageCategories = await storage.get("categories");
    if (storageCategories) {
      dispatch(updateCategories({ categories: JSON.parse(storageCategories) }));
    }
    fetchCategoriesByOrganization(organization.id, "")
      .then((res) => {
        if (res.data) {
          const categories = res.data;
          storage.set("categories", JSON.stringify(categories));
          dispatch(updateCategories({ categories }));
        }
      })
      .catch((err) => {
        console.log("Error while fetching categories!");
      });
  };

  const getDeliveries = async () => {
    const storageDeliveries = await storage.get("deliveries");
    if (storageDeliveries) {
      dispatch(updateDeliveries({ deliveries: JSON.parse(storageDeliveries) }));
    }
    fetchDeliveries(organization.id)
      .then((res) => {
        if (res.data) {
          const deliveries = res.data;
          storage.set("deliveries", JSON.stringify(deliveries));
          dispatch(updateDeliveries({ deliveries: deliveries }));
        }
      })
      .catch((err) => {
        console.log("Error while fetching deliveries!");
      });
  };

  const getTaxRate = async () => {
    const storageTaxRate = await storage.get("taxRate");
    if (storageTaxRate) {
      dispatch(updateTaxRate(storageTaxRate));
    }
    fetchTaxRate(organization.id)
      .then((res) => {
        if (res.data) {
          const taxRatePayload = {
            tax_rate: res.data,
          };
          storage.set("taxRate", JSON.stringify(taxRatePayload));
          dispatch(updateTaxRate(taxRatePayload));
        }
      })
      .catch((err) => {
        console.log("Error while fetching tax rate!");
      });
  };

  const getSubscriptionPlans = async () => {
    const storagePlans = await storage.get("plans");
    if (storagePlans) {
      let plans = JSON.parse(storagePlans);
      plans = plans?.length > 0 ? plans : [];
      dispatch(updateSubscriptionPlans({ plans: plans }));
    }

    if (!loadingUser && user.token) {
      fetchSubscriptionPlans()
        .then((res) => {
          if (res.data?.length > 0) {
            storage.set("plans", JSON.stringify(res.data));
            dispatch(updateSubscriptionPlans({ plans: res.data }));
          }
        })
        .catch((err) => {
          console.log("Error while fetching subscription plans.", err);
        });
    }

    const storageFeatures = await storage.get("features");
    if (storageFeatures) {
      let features = JSON.parse(storageFeatures);
      features = features?.length > 0 ? features : [];
      dispatch(updateSubscriptionFeatures({ features: features }));
    }

    if (!loadingUser && user.token) {
      fetchSubscriptionFeatures()
        .then((res) => {
          if (res.data?.length > 0) {
            storage.set("features", JSON.stringify(res.data));
            dispatch(updateSubscriptionFeatures({ features: res.data }));
          }
        })
        .catch((err) => {
          console.log("Error while fetching subscription features!", err);
        });
    }
  };

  useEffect(() => {
    getUserDetails();
  }, []);

  useEffect(() => {
    getSubscriptionPlans();
    getOrganization();
  }, [loadingUser, user.token]);

  useEffect(() => {
    organization.id && getCategories();
    organization.id && getTaxRate();
    organization.id && getDeliveries();
    if (organization.id) {
      const organization_id = organization.id;
      dispatch(fetchCustomerData({ organization_id }));
    }
  }, [organization.id]);

  useEffect(() => {
    const organization_id = organization.id;
    const branch_id = branch.id;
    if (organization_id && branch_id) {
      dispatch(fetchSalesData({ organization_id, branch_id }));
      dispatch(fetchProductData({ organization_id, branch_id }));
    }
  }, [organization.id, branch.id]);

  useEffect(() => {
    const handleOnline = async () => {
      let offlineSales = await storage.get("offlineSales");
      offlineSales = offlineSales ? JSON.parse(offlineSales) : [];

      let offlineOrders = await storage.get("offlineOrders");
      offlineOrders = offlineOrders ? JSON.parse(offlineOrders) : [];

      if (offlineSales?.length > 0 || offlineOrders?.length > 0) {
        const isOnline = await isActuallyOnline(true);

        if (isOnline) {
          transferQueuedData(dispatch);
          dispatch(updateNetworkStatus({ status: "online" }));
        } else {
          dispatch(updateNetworkStatus({ status: "offline" }));
        }
      } else {
        const isOnline = await isActuallyOnline();

        if (isOnline) {
          dispatch(updateNetworkStatus({ status: "online" }));
        } else {
          dispatch(updateNetworkStatus({ status: "offline" }));
        }
      }
    };

    const handleOffline = () => {
      dispatch(updateNetworkStatus({ status: "offline" }));
    };

    handleOnline();
    const interval = setInterval(handleOnline, 10 * 60 * 1000);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      clearInterval(interval);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [dispatch]);

  return (
    <>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          draggable
          draggablePercent={25}
          theme="light"
        />
        {/* <PullToRefresh /> */}
        <GoogleAnalytics />
        <PageTracker />
        <UpgradeAd />
        <FreeTrail />
        <Onboard />
        <Tour />
        <Cookies />
        <NetworkTab status={networkStatus} />
        <Navbar />
        <div className="pages-wrapper">
          <ContactSupportModel />
          <div className="pages-container">
            <Routes>
              <Route
                exact
                path={ROUTES.login.path}
                element={<Login setLoadingUser={setLoadingUser} />}
              />
              <Route
                exact
                path={ROUTES.register.path}
                element={<Register setLoadingUser={setLoadingUser} />}
              />
              <Route
                exact
                path={ROUTES.contactDUS.path}
                element={<Contactus outside={true} />}
              />
              <Route
                exact
                path={ROUTES.forgotPassword.path}
                element={<ForgotPassword />}
              />
              <Route
                exact
                path={ROUTES.resetPassword.path}
                element={<ResetPassword />}
              />
              <Route
                exact
                path={ROUTES.verifyOTP.path}
                element={<VerifyOTP />}
              />

              <Route element={<ProtectedRoutes loading={loadingUser} />}>
                <Route
                  exact
                  path={ROUTES.contactUs.path}
                  element={<Contactus />}
                />
                <Route exact path={ROUTES.help.path} element={<Help />} />
                <Route exact path={ROUTES.home.path} element={<Home />} />
                <Route exact path={ROUTES.profile.path} element={<Profile />} />
                <Route
                  exact
                  path={ROUTES.addOrganization.path}
                  element={<AddOrganization />}
                />

                <Route
                  element={
                    <OrganizationRoutes loading={organization.loading} />
                  }
                >
                  <Route
                    exact
                    path={ROUTES.support.path}
                    element={<Support />}
                  />
                  <Route
                    exact
                    path={ROUTES.supportDetails.path}
                    element={<SupportDetail />}
                  />
                  <Route
                    exact
                    path={ROUTES.myOrganization.path}
                    element={<MyOrganization />}
                  />
                  <Route
                    exact
                    path={ROUTES.editOrganization.path}
                    element={<AddOrganization isEdit={true} />}
                  />
                  <Route
                    exact
                    path={ROUTES.upgrade.path}
                    element={<Upgrade />}
                  />
                  <Route
                    exact
                    path={ROUTES.upgradeLearnMore.path}
                    element={<LearnMore />}
                  />

                  <Route
                    exact
                    path={ROUTES.upgradePayment.path}
                    element={<UpgradePayment />}
                  />
                  <Route
                    exact
                    path={ROUTES.manageBranch.path}
                    element={<ManageBranch />}
                  />
                  <Route
                    exact
                    path={ROUTES.categories.path}
                    element={<Categories />}
                  />
                  <Route
                    exact
                    path={ROUTES.addCategory.path}
                    element={<AddCategory />}
                  />
                  <Route
                    exact
                    path={ROUTES.editCategory.path}
                    element={<AddCategory isEdit={true} />}
                  />
                  <Route
                    exact
                    path={ROUTES.settings.path}
                    element={<Setting />}
                  />
                  <Route
                    exact
                    path={ROUTES.ordersSalesSettings.path}
                    element={<OrdersSales />}
                  />
                  <Route
                    exact
                    path={ROUTES.generalSettings.path}
                    element={<GeneralSetting />}
                  />
                  <Route
                    exact
                    path={ROUTES.currencySettings.path}
                    element={<Currency />}
                  />

                  <Route
                    exact
                    path={ROUTES.deliverySettings.path}
                    element={<DeliveryRates />}
                  />
                  <Route
                    exact
                    path={ROUTES.addDeliverySettings.path}
                    element={<AddDelivery />}
                  />
                  <Route
                    exact
                    path={ROUTES.editDeliverySettings.path}
                    element={<AddDelivery isEdit={true} />}
                  />
                  <Route
                    exact
                    path={ROUTES.paymentSettings.path}
                    element={<PaymentOption />}
                  />

                  <Route
                    exact
                    path={ROUTES.customers.path}
                    element={<Customer />}
                  />
                  <Route
                    exact
                    path={ROUTES.paymentCustomer.path}
                    element={<Customer isPayment={true} />}
                  />
                  <Route
                    exact
                    path={ROUTES.saleCustomer.path}
                    element={<Customer isSale={true} />}
                  />

                  <Route
                    exact
                    path={ROUTES.addCustomer.path}
                    element={<AddCustomer />}
                  />
                  <Route
                    exact
                    path={ROUTES.editCustomer.path}
                    element={<AddCustomer isEdit={true} />}
                  />
                  <Route
                    exact
                    path={ROUTES.paymentAddCustomer.path}
                    element={<AddCustomer isPayment={true} />}
                  />
                  <Route
                    exact
                    path={ROUTES.saleAddCustomer.path}
                    element={<AddCustomer isSale={true} />}
                  />
                  <Route
                    exact
                    path={ROUTES.customerDetails.path}
                    element={<CustomerDetails />}
                  />
                  <Route
                    exact
                    path={ROUTES.customerStoreCredit.path}
                    element={<StoreCredit />}
                  />

                  <Route path={ROUTES.manageUser.path} element={<Users />} />

                  <Route
                    path={ROUTES.addUser.path}
                    element={<AddUser isAdd={true} />}
                  />

                  <Route
                    path={ROUTES.manageUserBranches.path}
                    element={<UserBranches />}
                  />

                  <Route
                    path={ROUTES.addUserPassword.path}
                    element={<CreatePassword />}
                  />

                  <Route
                    path={ROUTES.changeUserPassword.path}
                    element={<CreatePassword />}
                  />

                  <Route
                    path={ROUTES.editUser.path}
                    element={<AddUser isEdit={true} />}
                  />

                  <Route path={ROUTES.analytics.path} element={<Analytics />}>
                    <Route index element={<MainAnalytics />} />
                    <Route
                      path={ROUTES.revenueAnalytics.innerPath}
                      element={<RevenueAnalytics />}
                    />
                    <Route
                      path={ROUTES.salesAnalytics.innerPath}
                      element={<SalesAnalytics />}
                    />
                    <Route
                      path={ROUTES.salesByStaffAnalytics.innerPath}
                      element={<SalesByStaffAnalytics />}
                    />
                    <Route
                      path={ROUTES.salesByCustomerAnalytics.innerPath}
                      element={<SalesByCustomerAnalytics />}
                    />
                    <Route
                      path={ROUTES.profitsAnalytics.innerPath}
                      element={<ProfitsAnalytics />}
                    />
                    <Route
                      path={ROUTES.paymentAnalytics.innerPath}
                      element={<PaymentAnalytics />}
                    />
                    <Route
                      path={ROUTES.productAnalytics.innerPath}
                      element={<ProductAnalytics />}
                    />
                    <Route
                      path={ROUTES.categoryAnalytics.innerPath}
                      element={<CategoryAnalytics />}
                    />
                    <Route
                      path={ROUTES.branchAnalytics.innerPath}
                      element={<BranchAnalysts />}
                    />
                  </Route>

                  <Route
                    element={<BranchRoutes loading={organization.loading} />}
                  >
                    <Route
                      exact
                      path={ROUTES.productsImport.path}
                      element={<Import name={"products"} />}
                    />
                    <Route
                      exact
                      path={ROUTES.customersImport.path}
                      element={<Import name={"customers"} />}
                    />
                    <Route
                      exact
                      path={ROUTES.addProduct.path}
                      element={<AddProduct />}
                    />
                    <Route
                      exact
                      path={ROUTES.editProduct.path}
                      element={<AddProduct isEdit={true} />}
                    />
                    <Route
                      exact
                      path={ROUTES.manageProduct.path}
                      element={<ManageProduct />}
                    />
                    <Route
                      exact
                      path={ROUTES.checkout.path}
                      element={<Checkout />}
                    />
                    <Route exact path={ROUTES.cart.path} element={<Cart />} />
                    <Route
                      exact
                      path={ROUTES.payment.path}
                      element={<Payment />}
                    />
                    <Route exact path={ROUTES.sales.path} element={<Sales />} />
                    <Route
                      exact
                      path={ROUTES.salesDetails.path}
                      element={<SalesDetails />}
                    />
                    <Route
                      exact
                      path={ROUTES.salesPdf.path}
                      element={<PDF />}
                    />
                    <Route
                      exact
                      path={ROUTES.orders.path}
                      element={<Orders />}
                    />
                    <Route
                      exact
                      path={ROUTES.activitiesSettings.path}
                      element={<Activities />}
                    />
                  </Route>

                  <Route path={ROUTES.aiChat.path} element={<AIChatPage />} />
                </Route>
              </Route>
            </Routes>
          </div>
        </div>
      </GoogleOAuthProvider>
    </>
  );
};

export default NewApp;

const OrganizationRoutes = ({ loading }) => {
  const organization = useSelector((state) => state.organizationReducer.value);

  return loading ? (
    <Loader />
  ) : organization.id ? (
    <Outlet />
  ) : (
    <Navigate to={ROUTES.addOrganization.path} replace />
  );
};

const BranchRoutes = ({ loading }) => {
  const branch = useSelector((state) => state.branchReducer.value);

  return loading ? (
    <Loader />
  ) : branch.id ? (
    <Outlet />
  ) : (
    <Navigate to={ROUTES.myOrganization.path} replace />
  );
};
